import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Checkbox, Tabs, Row, Col, Divider, Tooltip, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  RolesView,
  RolesActions,
  RolesInventColumns,
  RolesInventMoreInfo,
} from '@utils/rolesConstants';
import SessionContext from '@providers/Session/SessionContext';

import * as S from './styles';

const { TabPane } = Tabs;

const categoriesOrder = [
  'assets',
  'inventory',
  'operational',
  'administrative',
  'insights',
  'access',
  'financial',
  'support',
  'lorawan',
  'sms',
  'api',
  'public-api',
];

export type RoleProps = {
  id: string;
  name: string;
  category: string;
};

export type GroupedRolesProps = [string, RoleProps[]];

type FilterRoles = {
  category: string;
  roles: {
    name: string;
    visible: boolean;
  }[];
};

interface IPermissionsCardProps {
  groupedRoles: GroupedRolesProps[] | undefined;
  selectedRoles: string[] | undefined;
  setSelectedRoles: React.Dispatch<React.SetStateAction<any>>;
  filterRoles?: FilterRoles[];
}

const PermissionsCard: React.FC<IPermissionsCardProps> = ({
  groupedRoles,
  selectedRoles,
  setSelectedRoles,
  filterRoles,
}) => {
  const { t } = useTranslation(['permissions', 'roles', 'inventory']);
  const { isNotChildOfRoot } = useContext(SessionContext);
  const [checkedList, setCheckedList] = useState(selectedRoles);
  const [indeterminate, setIndeterminate] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [activeTab, setActiveTab] = useState<string>();

  const permissionsOptions = useMemo(() => {
    const allPermissions: string[] = [];
    const allGroups = groupedRoles?.map(([, permissions]) => permissions);
    allGroups?.forEach(group => {
      group.forEach(permission => allPermissions.push(permission.id));
    });
    return allPermissions;
  }, [groupedRoles]);

  useEffect(() => {
    setCheckedList(selectedRoles);
  }, [selectedRoles]);

  useEffect(() => {
    if (selectedRoles) {
      if (selectedRoles.length > 0) {
        setIndeterminate(true);
      } else if (selectedRoles.length >= permissionsOptions?.length) {
        setIndeterminate(false);
        setCheckAll(true);
      }
    }
  }, [permissionsOptions, selectedRoles]);

  const onCheckAllChange = useCallback(
    (checked: boolean) => {
      const newList = checked ? permissionsOptions : [];
      setCheckedList(newList);
      setSelectedRoles(newList);
      setIndeterminate(false);
      setCheckAll(checked);
    },
    [permissionsOptions, setSelectedRoles],
  );

  const onChange = useCallback(
    (list: any) => {
      setCheckedList(list);
      setSelectedRoles(list);
      setIndeterminate(
        !!list.length && list.length < permissionsOptions.length,
      );
      setCheckAll(list.length === permissionsOptions.length);
    },
    [permissionsOptions.length, setSelectedRoles],
  );

  const categorizedSectionsRoles = useCallback(
    (permissions: RoleProps[]) => {
      const roles: {
        viewRoles: RoleProps[];
        othersRoles: RoleProps[];
        manageRoles: RoleProps[];
        actionsRoles: RoleProps[];
        batchRoles: RoleProps[];
        inventColumnsRoles: RoleProps[];
        inventMoreInfoRoles: RoleProps[];
      } = {
        viewRoles: [],
        othersRoles: [],
        manageRoles: [],
        actionsRoles: [],
        batchRoles: [],
        inventColumnsRoles: [],
        inventMoreInfoRoles: [],
      };

      const allViewRoles: string[] = Object.values(RolesView);
      const allActionsRoles: string[] = Object.values(RolesActions);
      const allInventoryColumnsRoles: string[] =
        Object.values(RolesInventColumns);
      const allInventoryMoreInfoRoles: string[] =
        Object.values(RolesInventMoreInfo);

      permissions.forEach((role: RoleProps) => {
        if (
          ['priority-change', 'mot-deactivation', 'manage-sms'].includes(
            role.name,
          ) &&
          isNotChildOfRoot
        ) {
          return;
        }
        if (allViewRoles.includes(role.name) || role.name.includes('view')) {
          roles.viewRoles.push(role);
        } else if (role.name.includes('manage')) {
          roles.manageRoles.push(role);
        } else if (allActionsRoles.includes(role.name)) {
          roles.actionsRoles.push(role);
        } else if (role.name.includes('batch')) {
          roles.batchRoles.push(role);
        } else if (allInventoryColumnsRoles.includes(role.name)) {
          roles.inventColumnsRoles.push(role);
        } else if (allInventoryMoreInfoRoles.includes(role.name)) {
          roles.inventMoreInfoRoles.push(role);
        } else {
          roles.othersRoles.push(role);
        }
      });

      const finalRoles: any = {};

      Object.entries(roles).forEach(([type, rolesList]) => {
        if (rolesList.length > 0) {
          finalRoles[type] = rolesList;
        }
      });

      const finalRolesIsEmpty = Object.keys(finalRoles).length === 0;
      return !finalRolesIsEmpty && finalRoles;
    },
    [isNotChildOfRoot],
  );

  const categorizedRoles = useMemo(
    () =>
      groupedRoles
        ?.map(([category, permissions]) => {
          const filterRoleByCategory = filterRoles?.find(
            role => role.category === category,
          );
          let filteredPermissions = permissions;
          if (filterRoleByCategory) {
            filteredPermissions = permissions
              ?.map((permission: any) => {
                const filterRoleByPermission =
                  filterRoleByCategory?.roles?.find(
                    role => role.name === permission.name,
                  );
                if (filterRoleByPermission && !filterRoleByPermission.visible) {
                  return false;
                }
                return permission;
              })
              ?.filter((role: any) => role);
          }

          if (filteredPermissions?.length === 0) {
            return undefined;
          }

          return [
            category,
            {
              ...categorizedSectionsRoles(filteredPermissions),
            },
          ];
        })
        ?.filter(roles => roles)
        ?.sort((a, b) => {
          const aIndex = categoriesOrder.indexOf(a?.[0]);
          const bIndex = categoriesOrder.indexOf(b?.[0]);

          return (
            (aIndex === -1 ? categoriesOrder.length : aIndex) -
            (bIndex === -1 ? categoriesOrder.length : bIndex)
          );
        }),
    [groupedRoles, filterRoles, categorizedSectionsRoles],
  );

  const rolesSections = useMemo(
    () => [
      {
        title: t('sections.view'),
        listName: 'viewRoles',
      },
      {
        title: t('sections.manage'),
        listName: 'manageRoles',
      },
      {
        title: t('sections.actions'),
        listName: 'actionsRoles',
      },
      {
        title: t('sections.batch-actions'),
        listName: 'batchRoles',
      },
      {
        title: t('sections.inventory-columns'),
        listName: 'inventColumnsRoles',
      },
      {
        title: t('sections.inventory-more-info'),
        listName: 'inventMoreInfoRoles',
        help: t('sections.inventory-more-info-help'),
      },
      {
        title: t('sections.others'),
        listName: 'othersRoles',
      },
    ],
    [t],
  );

  const sortPermissions = useCallback(
    (permissions: { id: string; name: string }[], category: string) =>
      permissions
        ?.map(({ id, name }: { id: string; name: string }) => ({
          id,
          name: t(`${category}.permissions.${name}`),
        }))
        ?.sort((a, b) => a.name.localeCompare(b.name)),
    [t],
  );

  return (
    <S.PermissionCardContainer>
      <div
        className="permissions-card-header"
        style={{
          textAlign: 'right',
          marginBottom: 16,
        }}
      >
        <Checkbox
          indeterminate={indeterminate}
          onChange={({ target: { checked } }) => onCheckAllChange(checked)}
          checked={checkAll}
        >
          {t('roles:modal.select-all')}
        </Checkbox>
      </div>
      <Checkbox.Group
        style={{ width: '100%', marginBottom: 8 }}
        value={checkedList}
        onChange={onChange}
      >
        <Tabs
          activeKey={activeTab}
          tabBarGutter={30}
          // @ts-ignore
          onChange={setActiveTab}
        >
          {categorizedRoles?.map(category => {
            const categoryName = category?.[0];
            const permissions = category?.[1];
            return (
              <TabPane
                key={categoryName}
                tab={t(`${categoryName}.title`)}
                style={{
                  padding: '0 24px',
                }}
                forceRender
              >
                {rolesSections?.map(
                  ({ title, listName, help }) =>
                    permissions?.[listName]?.length > 0 && (
                      <Row
                        key={title}
                        gutter={[16, 8]}
                        style={{
                          width: '100%',
                        }}
                      >
                        <Divider
                          orientation="left"
                          className="permissions-card-divider"
                        >
                          {title.toUpperCase()}
                          {help && (
                            <Tooltip title={help}>
                              <Button
                                type="link"
                                icon={<InfoCircleOutlined />}
                              />
                            </Tooltip>
                          )}
                        </Divider>
                        {sortPermissions(
                          permissions?.[listName],
                          categoryName,
                        )?.map((role: any) => (
                          <Col key={role.id} sm={12} xs={24}>
                            <Checkbox value={role.id}>{role.name}</Checkbox>
                          </Col>
                        ))}
                      </Row>
                    ),
                )}
              </TabPane>
            );
          })}
        </Tabs>
      </Checkbox.Group>
    </S.PermissionCardContainer>
  );
};

export default memo(PermissionsCard);
